import c from 'classnames';

import { primaryFocusStyles, shadowFocusStyles } from '../utils/styles';
import { IconButtonAppearance, IconButtonVariant } from './types';

export const ICON_BUTTON_SIZE_CLASSES = {
  xxl: 'w-48 h-48',
  xl: 'w-40 h-40',
  l: 'w-36 h-36',
  m: 'w-32 h-32',
  s: 'w-28 h-28',
  xs: 'w-24 h-24',
  xxs: 'w-20 h-20',
};

export const ICON_BUTTON_SHAPE_CLASSES = {
  pill: 'rounded-full',
  square: 'rounded-[0.6rem]',
};

export const ICON_BUTTON_ICON_SIZE_CLASSES = {
  xxl: 'w-24 h-24',
  xl: 'w-24 h-24',
  l: 'w-20 h-20',
  m: 'w-20 h-20',
  s: 'w-20 h-20',
  xs: 'w-20 h-20',
  xxs: 'w-12 h-12',
};

type IconButtonClassesConfig = {
  [key in IconButtonVariant]: {
    [key in IconButtonAppearance]: { button: string; icon: string } | undefined;
  } & { default: { button: string; icon: string } };
};

const BG_PRIMARY_CLASSNAME = 'bg-bg-primary';
const OUTLINE_BORDER_CLASSNAME =
  'border border-solid border-border hover:border-border-medium active:border-border-dark';

const GHOST_BG_CLASSNAME = 'hover:bg-bg-hover active:bg-bg-onclick';

const ICON_BUTTON_CLASSES_CONFIG = {
  ghost: {
    blur: {
      button: c('hover:bg-interactive-blur active:bg-interactive-blur-hover', primaryFocusStyles),
      icon: 'text-icon-white',
    },
    primary: {
      button: c(GHOST_BG_CLASSNAME, primaryFocusStyles),
      icon: 'text-icon-active',
    },
    secondary: {
      button: c(GHOST_BG_CLASSNAME, primaryFocusStyles),
      icon: 'text-icon-secondary hover:text-icon-primary active:text-icon-active',
    },
  },
  outlined: {
    blur: {
      button: c(OUTLINE_BORDER_CLASSNAME, primaryFocusStyles),
      icon: 'text-icon-white',
    },
    primary: {
      button: c('bg-bg-primary', OUTLINE_BORDER_CLASSNAME, primaryFocusStyles),
      icon: 'text-icon-active',
    },
    secondary: {
      button: c(BG_PRIMARY_CLASSNAME, OUTLINE_BORDER_CLASSNAME, primaryFocusStyles),
      icon: 'text-icon-secondary',
    },
  },
  shadow: {
    blur: {
      button: c('bg-interactive-blur hover:bg-interactive-blur-hover', primaryFocusStyles),
      icon: 'text-icon-white active:text-icon-placeholder',
    },
    primary: {
      button: c(BG_PRIMARY_CLASSNAME, 'shadow-01', shadowFocusStyles),
      icon: 'text-icon-active hover:text-icon-primary active:text-icon-secondary',
    },
    secondary: {
      button: c(BG_PRIMARY_CLASSNAME, 'shadow-01', shadowFocusStyles),
      icon: 'text-icon-secondary hover:text-icon-primary active:text-icon-active',
    },
  },
} as IconButtonClassesConfig;

interface Params {
  appearance: IconButtonAppearance;
  variant: IconButtonVariant;
}

const getDisabledClassName = (appearance: IconButtonAppearance, variant: IconButtonVariant) => {
  if (appearance === 'blur') {
    return 'disabled:text-icon-white disabled:opacity-50';
  }

  if (variant === 'shadow') {
    return 'disabled:text-icon-placeholder disabled:opacity-50';
  }

  return 'disabled:text-icon-placeholder';
};

export const getIconButtonColorClasses = (params: Params) => {
  const { appearance, variant } = params;
  const config = ICON_BUTTON_CLASSES_CONFIG[variant];
  const { button, icon } = config[appearance] || config.default;

  return c(button, icon, getDisabledClassName(appearance, variant));
};
